import { FC, useMemo } from 'react';

import {
	EvSection,
	EvSwitch,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import { MOBILE_SDK_TEST_FILTERS_SECTION_ID } from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { useIncludeZeroIssueToggle } from 'src/apps/mobile-common/hooks/useIncludeZeroIssueToggle';
import { MOBILE_SCANS_FILTERS_LABELS } from 'src/apps/mobile-common/labels/MobileScansFilters.labels';
import { useMobileSdkSingleTestFilters } from 'src/apps/mobile-sdk/hooks/useMobileSdkSingleTestFilters';
import { useMobileSdkTestFilterOptionsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestFilterOptionsQuery';
import { TMobileSdkTestFilterOptions } from 'src/apps/mobile-sdk/types/MobileSdkServiceTypes';
import { ProductFiltersPanel } from 'src/common/components/pinned-date-range-filters-panel';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	buildPanelInitialValues,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';

interface IMobileSdkSingleTestFiltersProps {
	testId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	tableId: string;
	ariaLabel: string;
	showIncludeZeroIssueToggle?: boolean;
	includeZeroIssueToggleDataAutomationId?: string;
}

export const MobileSdkSingleTestFiltersSection: FC<IMobileSdkSingleTestFiltersProps> = ({
	testId,
	filters,
	onFilterChange,
	tableId,
	ariaLabel,
	showIncludeZeroIssueToggle = false,
	includeZeroIssueToggleDataAutomationId
}) => {
	const filterOptionsQuery = useMobileSdkTestFilterOptionsQuery(testId);

	const { filterDefinitions, mergedFilterOptions } = useMobileSdkSingleTestFilters({
		allLabels: filterOptionsQuery.data?.scans ?? [],
		selectedLabels: ((filters as TMobileSdkTestFilterOptions)?.scans as OptionType[]) ?? [],
		filterOptions: filterOptionsQuery.data ?? {}
	});

	const { includeScansWithoutIssues, handleApply, handleReset, handleToggleChange } =
		useIncludeZeroIssueToggle({ tableId, filters, onFilterChange, showIncludeZeroIssueToggle });

	const panelDefinitions = useMemo(
		() => mapToPanelDefinitions(filterDefinitions),
		[filterDefinitions]
	);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filters),
		[panelDefinitions, filters]
	);

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="filters-section"
			dataAutomationId={MOBILE_SDK_TEST_FILTERS_SECTION_ID}
		>
			<ProductFiltersPanel
				appElement={PLATFORM_APP_ID}
				onApply={handleApply}
				onReset={handleReset}
				initialValues={initialValues}
				filterOptions={mergedFilterOptions}
				filterDefinitions={panelDefinitions}
			/>
			{showIncludeZeroIssueToggle && (
				<EvSwitch
					dataAutomationId={includeZeroIssueToggleDataAutomationId}
					onChange={handleToggleChange}
					checked={includeScansWithoutIssues}
					label={MOBILE_SCANS_FILTERS_LABELS.INCLUDE_ZERO_ISSUE_TOGGLE_LABEL}
				/>
			)}
		</EvSection>
	);
};
